import FlowObject from "../../model/FlowObject";
import TextFlowObject from "../../model/TextFlowObject";
import ShapeFlowObject from "../../model/ShapeFlowObject";
import PdfFlowObject from "../../model/PdfFlowObject";
import ImageFlowObject from "../../model/ImageFlowObject";
import VideoFlowObject from "../../model/VideoFlowObject";
import ImageAsset from "../../model/Assets";
import Screens, { UploadResponse, OrderShift } from "../../model/Screens";
import Flow from "../../model/Flow";
import Color from "../../model/Color";
import FontArray, { Alignment } from "../../model/FontAttrsArray";
import Action from "../../model/Action";
import GalleryAsset from "../../model/GalleryAsset";
import { Actor, AnyEventObject } from "xstate";
import WebFlowObject from "../../model/WebFlowObject";
import GalleryFlowObject from "../../model/GalleryFlowObject";
import { GradientDef } from "../../ai/styles/themeDecorations";

// FlowMachine<FlowMachineContext, FlowMachineSchema, FlowEvents>

/**
 * Xstate machine has 3 states
 */
export interface FlowMachineSchema {
  states: {
    logic: LogicStateSchema;
    rendering: RenderStateSchema;
    textboxes: TextboxStateSchema;
  };
}

export type FlowMachineTypeState = {
  value: { 
    logic: "idle" | "loaded" | "undo_redo" | "complete", 
    textboxes: "inactive" | "active", 
  }
  context: FlowMachineContext
}


/**
 * Context keeps track of entire flow, current index, undo, redo, drafts, currentObject, publishing
 */
export interface FlowMachineContext {
  index: number;
  flow: Flow;
  undoStack: {
    undo: UndoEvents;
    redo: UndoEvents;
    id: string;
    kind: EventKind;
  }[];
  redoStack: {
    undo: UndoEvents;
    redo: UndoEvents;
    id: string;
    kind: EventKind;
  }[];
  drafts: {
    obj: DraftObjects;
    screen: number;
    ref?: Actor<any, AnyEventObject>;
  }[];
  currentObject: FlowObjects | null;
  loading: string[];
  parentService?: any;
  sendAction?: {
    undo: UndoEvents;
    redo: UndoEvents;
    id: string;
    kind: EventKind;
  };
  publishConfig: {
    count: number;
    asDraft: boolean;
    forceToView: boolean;
    viewLocation: string;
    total: number;
  };
  textboxId?: string;
  isInteracting: boolean;
}

export interface LogicStateSchema {
  states: {
    idle: {};
    loaded: {};
    undo_redo: {};
    complete: {};
  };
}

export interface RenderStateSchema {
  states: {
    idle: {};
    loading: {};
    loaded: {};
  };
}

export interface TextboxStateSchema {
  states: {
    inactive: {};
    active: {};
  };
}

// MARK: Media Machine Context and Schema
// MediaMachine<  MediaMachineContext,ChildMachineSchema,ChildEvents>

export interface ChildMachineSchema {
  states: {
    idle: {};
    active: {};
  };
}

/**
 * Sub-machine keeps track of a {@link FlowObjects}, view size, and action to be sent up to parent
 */
export interface MediaMachineContext {
  mmo: FlowObjects;
  viewSize: { width: number; height: number };
  sendAction?: {
    undo: UndoEvents;
    redo: UndoEvents;
    id: string;
    kind: EventKind;
  };
  screenIndex: number;
  parentSend?: (event: any) => void; // Callback to replace sendParent()
}

// MARK: Shared Types

/**
 * All possible variants of a {@link FlowObject}
 */
export type FlowObjects =
  | FlowObject
  | ShapeFlowObject
  | TextFlowObject
  | ImageFlowObject
  | PdfFlowObject
  | VideoFlowObject
  | WebFlowObject
  | GalleryFlowObject;

export interface Kind {
  eventKind(): EventKind;
}

/**
 * Draft objects are either a {@link FlowObjects} or just the ImageAsset itself
 */
export type DraftObjects =
  | { tag: "mmo"; value: FlowObjects }
  | { tag: "asset"; value: ImageAsset };

/**
 * Event `ADD_MMO` adds to a flow the passed {@link FlowObjects}. If the object requires uploading, it includes a draft object too.
 */
export type AddFlowObjectEvent = {
  type: "ADD_MMO";
  mmo: FlowObjects;
  // NOTE: All uploadable objects MUST be `File` types and not base64 string
  draft?: FlowObjects;
  screenIndex: number;
};

/**
 * Event `ADD_BACKGROUND_MMO` inserts an MMO at index 0 (back of z-order) for background use.
 */
export type AddBackgroundMMOEvent = {
  type: "ADD_BACKGROUND_MMO";
  mmo: FlowObjects;
  draft?: FlowObjects;
  screenIndex: number;
};

export enum EventKind {
  Screen,
  Mmo,
  Flow,
}

/**
 * When uploading objects, we need to know what value to update since the server response includes no context
 */
export enum IndexTypes {
  previewScreenShot = "previewScreenShot",
  originalImageAsset = "originalImageAsset",
  croppedImageAsset = "croppedImageAsset",
  pdfAsset = "pdfAsset",
  videoAsset = "videoAsset",
  galleryAsset = "galleryAsset",
}

export type IndexName = IndexTypes | string;

export enum ReorderKind {
  Drag,
  Copy,
  Delete,
}

export type UndoableFlowMachineEvents =
  | { type: "OVERRIDE_FLOW"; flow: Flow }
  | { type: "OVERRIDE_SCREEN"; screen: Screens; screenIndex?: number }
  | { type: "DELETE"; id: string, screenIndex: number }
  | { type: "SET_COLOR"; color: Color }
  | {
      type: "REORDER_SCREENS";
      screens: Screens[];
      newIndex?: number;
      kind?: ReorderKind;
    }
  | { type: "REORDER_MMOS"; direction: OrderShift; id: string }
  | { type: "CHANGE_TITLE"; text: string }
  | AddFlowObjectEvent
  | AddBackgroundMMOEvent;

export type FlowMachineEvents =
  | { type: "SET_INDEX"; index: number }
  | { type: "SET_CURRENT_OBJECT"; id: string }
  | { type: "SET_PARENT_SERVICE"; service: any }
  | { type: "SET_INTERACTING"; value: boolean }
  | {
      type: "ADD_UNDO_REDO";
      actions: {
        undo: UndoEvents;
        redo: UndoEvents;
        id: string;
        kind: EventKind;
      };
    }
  | { type: "UNDO" }
  | { type: "REDO" }
  | { type: "UNDO_CLEANUP" }
  | { type: "REDO_CLEANUP" }
  | { type: "ADD_DRAFT"; object: DraftObjects; index: number }
  | { type: "CLEAR_DRAFTS" }
  | { type: "DELEGATE"; payload: ChildEvents; id: string }
  | { type: "MARK_ALL_INACTIVE" }
  | {
      type: "UPLOAD_DRAFTS";
      asDraft: boolean;
      forceToView: boolean;
      viewLocation?: string;
    }
  | {
      type: "UPDATE_URLS";
      screen: number;
      id: string;
      kind: EventKind;
      uploadResponse: UploadResponse[];
      indexMap: Map<IndexName, number>;
    }
  | { type: "OVERRIDE_SCREEN_PREVIEW"; image: ImageAsset, screenIndex: number }
  | { type: "COMMIT"; mmo: FlowObjects; id: string; shouldDraft: boolean, screenIndex: number }
  | { type: "PUBLISH" }
  | UndoableFlowMachineEvents
  | AddFlowObjectEvent
  | AddBackgroundMMOEvent;

export type SharedChildEvents =
  | { type: "SET_POSITION"; x: number; y: number }
  | { type: "SET_SIZE"; width: number; height: number }
  | { type: "SET_SIZE_AND_POSITION"; width: number; height: number; x: number; y: number }
  | { type: "SET_ROTATION"; rotation: 0 | 1 | 2 | 3 }
  | { type: "SET_SCALE"; scale: number }
  | { type: "SET_ACTION"; action: Action | null }
  | { type: "ACTIVATE"; id: string }
  | { type: "DEACTIVATE"; id: string };

export type TextChildEvents =
  | { type: "SET_COLOR"; color: Color }
  | { type: "SET_ALIGN"; align: Alignment }
  | { type: "SET_FONT_NAME"; typeface: string }
  | { type: "SET_FONT_STYLE"; style: string }
  | {
      type: "EDIT_TEXT";
      attributes: FontArray[];
      text: string;
      isThemeText: boolean;
    }
  | { type: "SET_FONT_SIZE"; size: number };

export type AVChildEvents =
  | { type: "CHANGE_PREVIEW_IMG"; img: ImageAsset }
  | { type: "SET_CROP"; img: ImageAsset }
  | { type: "SET_CORNER_RADIUS"; radius: number }
  | {type: "SET_BORDER", color: Color, width: number};

export type ShapeChildEvents =
  | { type: "SET_GRADIENT"; gradient: GradientDef | null };

export type GalleryChildEvents =
  | { type: "CHANGE_GALLERY_ASSET"; gallery: GalleryAsset }
  | AVChildEvents;

export type VideoChildEvents =
  | { type: "SET_AUTOPLAY"; to: boolean }
  | AVChildEvents;

export type TextBoxEvents =
  | { type: "MARK_ACTIVE"; id: string }
  | { type: "MARK_INACTIVE" }
  | { type: "MARK_EDITOR_ACTIVE" };

export type ChildEvents =
  | SharedChildEvents
  | TextChildEvents
  | AVChildEvents
  | VideoChildEvents
  | GalleryChildEvents
  | ShapeChildEvents;

export type TextMediaEvents = SharedChildEvents | TextChildEvents;

export type AVMediaEvents = SharedChildEvents | AVChildEvents;

export type VideoMediaEvents = AVMediaEvents | VideoChildEvents;

export type UndoEvents = ChildEvents | UndoableFlowMachineEvents;

export type FlowEvents = ChildEvents | FlowMachineEvents | TextBoxEvents;

// MARK: CONSTANTS
export const SET_BORDER: 'SET_BORDER' = 'SET_BORDER';
export const SET_CORNER_RADIUS: "SET_CORNER_RADIUS" = "SET_CORNER_RADIUS";
export const MARK_EDITOR_ACTIVE: "MARK_EDITOR_ACTIVE" = "MARK_EDITOR_ACTIVE";
export const UPLOAD_DRAFTS: "UPLOAD_DRAFTS" = "UPLOAD_DRAFTS";
export const UPDATE_URLS: "UPDATE_URLS" = "UPDATE_URLS";
export const PUBLISH: "PUBLISH" = "PUBLISH";
export const CHANGE_GALLERY_ASSET: "CHANGE_GALLERY_ASSET" =
  "CHANGE_GALLERY_ASSET";
export const EDIT_TEXT: "EDIT_TEXT" = "EDIT_TEXT";
export const MARK_ACTIVE: "MARK_ACTIVE" = "MARK_ACTIVE";
export const MARK_INACTIVE: "MARK_INACTIVE" = "MARK_INACTIVE";
export const SET_ROTATION: "SET_ROTATION" = "SET_ROTATION";
export const MARK_LOADED: "MARK_LOADED" = "MARK_LOADED";
export const SET_AUTOPLAY: "SET_AUTOPLAY" = "SET_AUTOPLAY";
export const MARK_ALL_INACTIVE: "MARK_ALL_INACTIVE" = "MARK_ALL_INACTIVE";
export const ACTIVATE: "ACTIVATE" = "ACTIVATE";
export const DEACTIVATE: "DEACTIVATE" = "DEACTIVATE";
export const ADD_MMO: "ADD_MMO" = "ADD_MMO";
export const SET_INDEX: "SET_INDEX" = "SET_INDEX";
export const OVERRIDE_FLOW: "OVERRIDE_FLOW" = "OVERRIDE_FLOW";
export const OVERRIDE_SCREEN: "OVERRIDE_SCREEN" = "OVERRIDE_SCREEN";
export const SET_CURRENT_OBJECT: "SET_CURRENT_OBJECT" = "SET_CURRENT_OBJECT";
export const ADD_UNDO_REDO: "ADD_UNDO_REDO" = "ADD_UNDO_REDO";
export const UNDO: "UNDO" = "UNDO";
export const REDO: "REDO" = "REDO";
export const CHANGE_TITLE: "CHANGE_TITLE" = "CHANGE_TITLE";
export const ADD_DRAFT: "ADD_DRAFT" = "ADD_DRAFT";
export const CLEAR_DRAFTS: "CLEAR_DRAFTS" = "CLEAR_DRAFTS";
export const REORDER_SCREENS: "REORDER_SCREENS" = "REORDER_SCREENS";
export const REORDER_MMOS: "REORDER_MMOS" = "REORDER_MMOS";
export const DELEGATE: "DELEGATE" = "DELEGATE";
export const SET_POSITION: "SET_POSITION" = "SET_POSITION";
export const SET_SIZE: "SET_SIZE" = "SET_SIZE";
export const SET_SIZE_AND_POSITION: "SET_SIZE_AND_POSITION" = "SET_SIZE_AND_POSITION";
export const SET_SCALE: "SET_SCALE" = "SET_SCALE";
export const SET_ACTION: "SET_ACTION" = "SET_ACTION";
export const SET_COLOR: "SET_COLOR" = "SET_COLOR";
export const SET_ALIGN: "SET_ALIGN" = "SET_ALIGN";
export const SET_FONT_NAME: "SET_FONT_NAME" = "SET_FONT_NAME";
export const SET_FONT_STYLE: "SET_FONT_STYLE" = "SET_FONT_STYLE";
export const SET_FONT_SIZE: "SET_FONT_SIZE" = "SET_FONT_SIZE";
export const CHANGE_PREVIEW_IMG: "CHANGE_PREVIEW_IMG" = "CHANGE_PREVIEW_IMG";
export const OVERRIDE_SCREEN_PREVIEW: "OVERRIDE_SCREEN_PREVIEW" =
  "OVERRIDE_SCREEN_PREVIEW";
export const SET_CROP: "SET_CROP" = "SET_CROP";
export const DELETE: "DELETE" = "DELETE";
export const COMMIT: "COMMIT" = "COMMIT";
export const SET_INTERACTING: "SET_INTERACTING" = "SET_INTERACTING";
export const SET_GRADIENT: "SET_GRADIENT" = "SET_GRADIENT";
export const ADD_BACKGROUND_MMO: "ADD_BACKGROUND_MMO" = "ADD_BACKGROUND_MMO";
